/**
 * Load governed PDF bytes through PDF.js without a second source fetch.
 */

import pdfjsWorkerSrc from "pdfjs-dist/legacy/build/pdf.worker.js?url";

export type PdfJsTextItem = {
  str: string;
  transform?: number[];
  width?: number;
  height?: number;
};

export type PdfJsTextContent = {
  items: PdfJsTextItem[];
};

export type PdfJsPageView = {
  getViewport: (input: { scale: number }) => {
    width: number;
    height: number;
    convertToViewportPoint: (x: number, y: number) => [number, number];
  };
  render: (input: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void>; cancel: () => void };
  getTextContent: () => Promise<PdfJsTextContent>;
};

export type PdfJsDocumentView = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfJsPageView>;
  destroy?: () => void;
};

type PdfJsModule = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (input: { data: Uint8Array }) => {
    promise: Promise<PdfJsDocumentView>;
  };
};

let governedPdfJsWorkerConfigured = false;

export function configureGovernedPdfJsWorker(pdfjs: PdfJsModule): void {
  if (governedPdfJsWorkerConfigured) {
    return;
  }
  pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerSrc;
  governedPdfJsWorkerConfigured = true;
}

export async function loadPdfJsModule(): Promise<PdfJsModule> {
  const pdfjsModule = await import("pdfjs-dist/legacy/build/pdf.js");
  const pdfjs =
    (pdfjsModule as unknown as { default?: PdfJsModule }).default ??
    (pdfjsModule as unknown as PdfJsModule);
  configureGovernedPdfJsWorker(pdfjs);
  return pdfjs;
}

export async function loadGovernedPdfDocumentFromBlob(
  blob: Blob,
): Promise<PdfJsDocumentView> {
  const pdfjs = await loadPdfJsModule();
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return pdfjs.getDocument({ data: bytes }).promise;
}

export function resolvePdfTextItemStyle(input: {
  item: PdfJsTextItem;
  viewport: {
    convertToViewportPoint: (x: number, y: number) => [number, number];
  };
}): {
  left: number;
  top: number;
  fontSize: number;
} {
  const transform = input.item.transform ?? [1, 0, 0, 1, 0, 0];
  const [left, top] = input.viewport.convertToViewportPoint(
    transform[4] ?? 0,
    transform[5] ?? 0,
  );
  const fontSize = Math.hypot(transform[0] ?? 1, transform[1] ?? 0);
  return { left, top, fontSize };
}
